import {create} from 'zustand';
import {getUserPermissions, Permission} from "../clients/metoro/rbac";
import {Featureflags, getFeatureFlags} from "../clients/metoro/featureflags";
import {dashboardJsonReplacer, dashboardJsonReviver} from "../components/Dashboarding/Dashboard";

const STORAGE_KEY = 'metoro-featureflags';
const STORAGE_KEY_LAST_LOAD = 'metoro-featureflags-last-load';

interface FeatureFlagsStore {
    // The unix timestamp in seconds of the last load
    lastLoad: number;
    isLoading: boolean;
    isInitialized: boolean;
    error: string | null;
    loadFeatureFlags: () => Promise<Featureflags | undefined>;
    featureFlagEnabled: (featureFlag: string) => Promise<boolean>;
}


// Try to get config from localStorage
const getStoredFeatureFlags = (): Featureflags | null => {
    try {
        const lastLoad = localStorage.getItem(STORAGE_KEY_LAST_LOAD)
        //
        if (!lastLoad || (new Date().getTime() / 1000 - Number(lastLoad) > 300)) {
            return null;
        }
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            return JSON.parse(stored, dashboardJsonReviver);
        }
    } catch (error) {
        console.error('Failed to parse stored config:', error);
    }
    return null
};

function featureFlagEnabled(featureFlags: Featureflags | undefined, featureFlag: string): boolean {
    if (!featureFlags) {
        return false;
    }
    return featureFlags.booleanFlags.get(featureFlag) || false;
}

export const useFeatureFlagsStore = create<FeatureFlagsStore>((set) => {
    const loadFeatureFlags = async () => {
        // If we already have a stored config, use it initially
        const storedPermissions = getStoredFeatureFlags();
        if (storedPermissions) {
            return storedPermissions
        }

        set({isLoading: true, error: null});
        try {
            const featureflags = await getFeatureFlags()

            // Store in localStorage
            localStorage.setItem(STORAGE_KEY, JSON.stringify(featureflags, dashboardJsonReplacer));
            localStorage.setItem(STORAGE_KEY_LAST_LOAD, (new Date().getTime() / 1000).toString());

            set({
                isLoading: false,
                isInitialized: true
            });
            return featureflags;
        } catch (error) {
            console.error('Failed to load Metoro configuration:', error);
            set({
                error: 'Failed to load configuration',
                isLoading: false,
                isInitialized: true,
                // If we have stored config, keep using it on error
            });
        }
    }

    return {
        isLoading: false,
        isInitialized: false,
        error: null,
        lastLoad: 0,
        loadFeatureFlags: loadFeatureFlags,
        featureFlagEnabled: async (featureFlag: string) => featureFlagEnabled(await loadFeatureFlags(), featureFlag)
    }
});